/**
 * @file
 * Lets the user resize the assistant panel, remembering the size.
 *
 * Not CSS `resize`: the widget is pinned to the bottom-right corner, so the
 * native handle (always on a bottom corner) would grow the panel away from the
 * pointer. The grip sits on the panel's top-left corner instead, where
 * dragging outwards and growing outwards are the same direction.
 *
 * The two custom properties are read by scss/components/_chatbot.scss, which
 * clamps them against the viewport - so nothing here needs to know the limits.
 */
const KEY = 'eudat:chatbot-size';

Drupal.behaviors.eudatChatbotResize = {
  attach(context) {
    const panel = context.querySelector?.('.eudat-chatbot__panel');
    if (!panel || panel.dataset.eudatResizeInit === '1') {
      return;
    }
    panel.dataset.eudatResizeInit = '1';

    const widget = panel.closest('.eudat-chatbot');
    const handle = widget?.querySelector('[data-eudat-chatbot-resize]');
    if (!handle) {
      return;
    }

    const apply = (width, height) => {
      widget.style.setProperty('--chatbot-panel-width', `${Math.round(width)}px`);
      widget.style.setProperty('--chatbot-panel-height', `${Math.round(height)}px`);
    };

    // Store what the panel ended up at, not what was asked for: the CSS clamp
    // may have refused part of the drag, and saving the raw request would make
    // the panel jump on the next page load.
    const remember = () => {
      const { width, height } = panel.getBoundingClientRect();
      // The panel is display:none while the widget is collapsed, so a stray
      // call here would persist 0x0 and the panel would come back stuck at its
      // minimum on the next load.
      if (!width || !height) {
        return;
      }
      try {
        localStorage.setItem(KEY, JSON.stringify({ width, height }));
      }
      catch {
        // Private mode or a full quota: the size just stops persisting.
      }
    };

    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (saved?.width > 0 && saved?.height > 0) {
        apply(saved.width, saved.height);
      }
    }
    catch {
      localStorage.removeItem(KEY);
    }

    let origin = null;

    handle.addEventListener('pointerdown', (event) => {
      const { width, height } = panel.getBoundingClientRect();
      if (!width || !height) {
        return;
      }
      origin = { x: event.clientX, y: event.clientY, width, height };
      handle.setPointerCapture(event.pointerId);
      event.preventDefault();
    });

    handle.addEventListener('pointermove', (event) => {
      if (!origin) {
        return;
      }
      // Pinned bottom-right, so up and left are the growing directions.
      apply(
        origin.width + (origin.x - event.clientX),
        origin.height + (origin.y - event.clientY),
      );
    });

    const release = () => {
      if (!origin) {
        return;
      }
      origin = null;
      remember();
    };
    handle.addEventListener('pointerup', release);
    handle.addEventListener('pointercancel', release);

    // Keyboard equivalent: a drag-only control is unusable without a pointer.
    handle.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 48 : 16;
      const delta = {
        ArrowLeft: [step, 0],
        ArrowRight: [-step, 0],
        ArrowUp: [0, step],
        ArrowDown: [0, -step],
      }[event.key];
      if (!delta) {
        return;
      }
      event.preventDefault();
      const { width, height } = panel.getBoundingClientRect();
      apply(width + delta[0], height + delta[1]);
      remember();
    });
  },
};
